import { Search } from "lucide-react";
import type { ReactNode } from "react";

import { Input } from "@/components/motion/input";

import {
  Activity,
  Fingerprint,
  Inbox,
  Key,
  Mails,
  PlugZap,
  Send,
  Settings,
  ShieldBan,
  Webhook,
  type LucideIcon,
} from "lucide-react";

import type { InboxDirection } from "@/features/inbox/filters";

/* ─── Types ──────────────────────────────────────────────── */

export type DashboardNavItem = {
  label: string;
  icon: LucideIcon;
  to: string;
};

/** A mailbox view in the left sidebar. Each one is the inbox route with a direction. */
export type DashboardFolder = {
  label: string;
  icon: LucideIcon;
  direction: InboxDirection;
};

/* ─── Constants ──────────────────────────────────────────── */

export const DASHBOARD_FONT_LINKS = [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous" as const,
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Chivo+Mono:wght@400;700&display=swap",
  },
];

export const DASHBOARD_FOLDERS: DashboardFolder[] = [
  { label: "Inbox", icon: Inbox, direction: "inbound" },
  { label: "Sent", icon: Send, direction: "outbound" },
  { label: "All mail", icon: Mails, direction: "all" },
];

/**
 * Everything that is not a mailbox: shown in the apps menu next to the logo and
 * in the right-hand rail.
 */
export const DASHBOARD_FEATURES: DashboardNavItem[] = [
  { label: "Identities", icon: Fingerprint, to: "/dashboard/identities" },
  { label: "Connected", icon: PlugZap, to: "/dashboard/connected-accounts" },
  { label: "Analytics", icon: Activity, to: "/dashboard/analytics" },
  { label: "Suppressions", icon: ShieldBan, to: "/dashboard/suppressions" },
  { label: "API Keys", icon: Key, to: "/dashboard/api-keys" },
  { label: "Webhooks", icon: Webhook, to: "/dashboard/webhooks" },
  { label: "Settings", icon: Settings, to: "/dashboard/settings" },
];

/* ─── Shared UI primitives ───────────────────────────────── */

/**
 * Blueprint-style decorative corner markers.
 * Purely visual — hidden from screen readers.
 */
export function CornerDots() {
  return (
    <span aria-hidden="true">
      <span className="absolute -top-[3px] -left-[3px] h-[6px] w-[6px] border border-hb-border-hover bg-hb-page" />
      <span className="absolute -top-[3px] -right-[3px] h-[6px] w-[6px] border border-hb-border-hover bg-hb-page" />
      <span className="absolute -bottom-[3px] -left-[3px] h-[6px] w-[6px] border border-hb-border-hover bg-hb-page" />
      <span className="absolute -bottom-[3px] -right-[3px] h-[6px] w-[6px] border border-hb-border-hover bg-hb-page" />
    </span>
  );
}

/**
 * Standard page header bar with icon, title, optional badges, and right-side controls.
 * Used by every dashboard page (Inbox, Identities, Webhooks, API Keys, etc.).
 */
export function PageHeader({
  icon,
  title,
  badges,
  children,
}: {
  icon: ReactNode;
  title: string;
  badges?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="border-b border-hb-border px-4 py-3.5 sm:px-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="shrink-0">{icon}</span>
          <h1 className="font-hb text-lg font-semibold tracking-tight text-hb-heading">{title}</h1>
          {badges}
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Card-style empty state with CornerDots, icon circle, heading, and description.
 * 7 occurrences across inbox, api-keys, webhooks, identities, layout.
 */
export function EmptyStateCard({
  icon,
  heading,
  description,
  action,
  maxWidth = "360px",
}: {
  icon: ReactNode;
  heading: string;
  description: string;
  action?: ReactNode;
  maxWidth?: string;
}) {
  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <div
        className="relative w-full border border-dashed border-hb-card bg-hb-surface px-10 py-12 text-center"
        style={{ maxWidth }}
      >
        <CornerDots />
        <div className="mx-auto mb-5 flex h-10 w-10 items-center justify-center rounded-full border border-dashed border-hb-card">
          {icon}
        </div>
        <h2 className="mb-1.5 font-hb text-sm font-bold text-hb-heading">{heading}</h2>
        <p className="font-mono-hb text-[11px] leading-relaxed text-hb-muted">{description}</p>
        {action ? <div className="mt-5">{action}</div> : null}
      </div>
    </div>
  );
}

/**
 * Bordered section card with CornerDots, optional titled header, and content area.
 * Extracted from settings.tsx SectionCard; used in ~20 locations.
 */
export function DashboardSectionCard({
  title,
  icon,
  action,
  children,
  className = "",
  border = "border-hb-card",
}: {
  title?: string;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  border?: string;
}) {
  return (
    <div className={`relative border border-dashed ${border} bg-hb-surface ${className}`}>
      <CornerDots />
      {title ? (
        <div
          className={`flex items-center justify-between border-b border-dashed ${border} px-5 py-3`}
        >
          <div className="flex items-center gap-2.5">
            {icon}
            <span className="font-mono-hb text-[10px] uppercase tracking-[0.15em] text-hb-muted">
              {title}
            </span>
          </div>
          {action}
        </div>
      ) : null}
      <div className={title ? "px-5 py-4" : "p-5"}>{children}</div>
    </div>
  );
}

/**
 * Search input with magnifying glass icon prefix.
 * Normalizes the 5 inconsistent search input implementations to one pattern.
 */
export function DashboardSearchInput({
  value,
  onChange,
  placeholder = "Search...",
  "aria-label": ariaLabel,
  className = "",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  "aria-label"?: string;
  className?: string;
}) {
  return (
    <Input
      type="search"
      size="sm"
      aria-label={ariaLabel || placeholder.replace(/\.\.\.$/, "")}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      leftIcon={<Search aria-hidden="true" />}
      className={className}
    />
  );
}

/* ─── Utilities ──────────────────────────────────────────── */

export function formatDate(iso: string, style: "short" | "full") {
  const date = new Date(iso);

  if (style === "short") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

export function getInitials(name: string) {
  if (!name || !name.trim()) return "?";
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Safely write to the clipboard with graceful failure handling.
 * Returns `true` on success, `false` on failure (e.g. insecure context,
 * no focus, or Clipboard API unavailable).
 */
export async function safeClipboardWrite(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
